<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import { toast } from 'vue-sonner';
    import { useI18n } from 'vue-i18n';

    import axios from 'axios';
    import DataTableSelect from '@/components/datatable/DataTableSelect.vue';

    import type { AcceptableValue } from 'reka-ui';
    import type { DataTableSelectOption, DashboardTableData } from '@/types';
    import type { Row } from '@tanstack/vue-table';

    const props = defineProps<{
        options: DataTableSelectOption[];
        row: Row<DashboardTableData>;
        columnId: keyof DashboardTableData;
        reportingPeriod: string;
    }>();

    const emit = defineEmits<{
        statusSaved: [
            reportingPeriod: string,
            companyId: number,
            updates: Partial<DashboardTableData>,
        ];
    }>();

    const { t } = useI18n();

    const currentValue = ref(props.row.original[props.columnId] as AcceptableValue);

    const routeName = computed(() => {
        return props.columnId === 'vat_status'
            ? 'v2.dashboard.vat.update'
            : 'v2.dashboard.status.update';
    });

    const handleStatusChange = async (newValue: AcceptableValue) => {
        const previousValue = currentValue.value;
        const companyId = props.row.original.id;

        if (!companyId) {
            return;
        }

        try {
            const selectedOption = props.options.find((opt) => opt.value === newValue);
            if (!selectedOption) return;

            currentValue.value = newValue;

            await axios.post(route(routeName.value), {
                company_id: companyId,
                data_field_id: selectedOption.value,
                data_field_group: props.columnId,
                data_field_approve: selectedOption.addon,
                reporting_period: props.reportingPeriod,
            });

            const updates: Partial<DashboardTableData> = {
                [props.columnId]: selectedOption.value,
            };

            if (props.columnId === 'pbc_status' && selectedOption.color === 'green') {
                await axios.post(
                    route('v2.dashboard.pbc-deadline.update', { company: companyId }),
                    {
                        reporting_period: props.reportingPeriod,
                        deadline: null,
                    },
                );

                updates.pbc_deadline = null as unknown as DashboardTableData['pbc_deadline'];
            }

            emit('statusSaved', props.reportingPeriod, companyId, updates);
        } catch (error) {
            currentValue.value = previousValue;
            console.error('Failed to update status:', error);
            toast.error(t('dashboards.status_update_failed'));
        }
    };

    // Keep local model in sync if outer data changes (e.g., details hydration or other edits)
    watch(
        () => props.row.original[props.columnId],
        (val) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            currentValue.value = val as AcceptableValue;
        },
    );
</script>

<template>
    <DataTableSelect
        :model-value="currentValue"
        :options="options"
        @update:model-value="handleStatusChange"
    />
</template>
